import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { FontSize } from "../../../../components/Text";
import { DashboardTodayEntrySourceDto } from "../../../../model/dashboard/dto/DashboardTodayEntrySourceDto";
import { TestIDs } from "../../../../tests/types/testIds";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { getEntrySourceLabel } from "../../DashboardUtils";
import { DashboardEmpty } from "../DashboardListRow";
import { DashboardPanel } from "../DashboardPanel";

const Bars = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.s};
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Bar = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 9rem) minmax(0, 1fr) 2rem;
  align-items: center;
  gap: ${Spacing.s};
  font-size: ${FontSize.s};
`;

const Label = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Track = styled.span`
  height: 0.6rem;
  border-radius: ${Rounded.pill};
  background-color: ${({ theme }) => theme.sub};
  overflow: hidden;
`;

const Fill = styled.span<{ $width: number }>`
  display: block;
  height: 100%;
  width: ${({ $width }) => `${$width}%`};
  border-radius: ${Rounded.pill};
  background-color: ${({ theme }) => theme.main};
`;

const Count = styled.span`
  text-align: end;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.darkerSub};
`;

type DashboardTodaySourcesProps = {
  sources: DashboardTodayEntrySourceDto[];
};

export const DashboardTodaySources: React.FC<DashboardTodaySourcesProps> = ({
  sources,
}) => {
  const { t } = useTranslation();
  const max = Math.max(...sources.map(({ count }) => count), 1);

  return (
    <DashboardPanel
      title={t("dashboard.today.sources.title")}
      subtitle={t("dashboard.today.sources.subtitle")}
      dataTestId={TestIDs.dashboard.today.sources}
    >
      {sources.length ? (
        <Bars>
          {sources.map((source) => {
            const label = getEntrySourceLabel(source, t);

            return (
              <Bar key={`${source.formId}-${source.origin || ""}`}>
                <Label title={label} dir="auto">
                  {label}
                </Label>
                <Track aria-hidden>
                  <Fill $width={(source.count / max) * 100} />
                </Track>
                <Count>{source.count}</Count>
              </Bar>
            );
          })}
        </Bars>
      ) : (
        <DashboardEmpty>{t("dashboard.today.sources.empty")}</DashboardEmpty>
      )}
    </DashboardPanel>
  );
};
